import React, {useState, useEffect} from 'react';
import {Link, useLocation, useNavigate} from 'react-router-dom';
import {motion, AnimatePresence} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiMenu, FiX, FiSettings, FiHome, FiCode, FiUser, FiBriefcase, FiEdit, FiMail} = FiIcons;

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleAdminAccess = () => {
    navigate('/admin');
  };

  const navItems = [
    {name: 'Home', path: '/', icon: FiHome},
    {name: 'Services', path: '/services', icon: FiCode},
    {name: 'About', path: '/about', icon: FiUser},
    {name: 'Case Studies', path: '/case-studies', icon: FiBriefcase},
    {name: 'Blog', path: '/blog', icon: FiEdit},
    {name: 'Contact', path: '/contact', icon: FiMail}
  ];

  return (
    <motion.nav
      initial={{y: -100}}
      animate={{y: 0}}
      className={`fixed w-full z-50 transition-all duration-500 ${
        scrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-xl border-b border-forest-200' 
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center space-x-3 group">
            <motion.div
              className="w-10 h-10 bg-gradient-to-br from-forest-600 to-forest-700 rounded-xl flex items-center justify-center shadow-lg border border-forest-500/20"
              whileHover={{scale: 1.1, rotate: 5}}
              transition={{type: "spring", stiffness: 300}}
            >
              <span className="text-white font-bold text-xl">A</span>
            </motion.div>
            <span className="text-xl font-bold bg-gradient-to-r from-forest-600 via-forest-700 to-forest-800 bg-clip-text text-transparent">
              aiximo
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-2">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`relative flex items-center space-x-2 px-4 py-2 rounded-xl font-medium transition-all duration-300 ${
                  location.pathname === item.path
                    ? 'text-white bg-gradient-to-r from-forest-600 to-forest-700 shadow-lg border border-forest-500/30'
                    : scrolled
                    ? 'text-gray-700 hover:text-forest-700 hover:bg-forest-50 border border-transparent hover:border-forest-200'
                    : 'text-gray-800 hover:text-forest-700 hover:bg-white/20 border border-transparent hover:border-forest-300/20'
                }`}
              >
                <SafeIcon icon={item.icon} className="h-4 w-4" />
                <span className="text-sm">{item.name}</span>
                {location.pathname === item.path && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute inset-0 bg-gradient-to-r from-forest-600 to-forest-700 rounded-xl -z-10 border border-forest-500/30"
                    transition={{type: "spring", stiffness: 300, damping: 30}}
                  />
                )}
              </Link>
            ))}

            {/* Admin Access Button */}
            <motion.button
              onClick={handleAdminAccess}
              className={`p-3 rounded-xl transition-all duration-300 border ${
                scrolled
                  ? 'text-gray-600 hover:text-forest-700 hover:bg-forest-50 border-transparent hover:border-forest-200'
                  : 'text-gray-700 hover:text-forest-700 hover:bg-white/20 border-transparent hover:border-forest-300/20'
              }`}
              whileHover={{scale: 1.1}}
              whileTap={{scale: 0.95}}
              title="Admin Access"
            >
              <SafeIcon icon={FiSettings} className="h-5 w-5" />
            </motion.button>

            <motion.div whileHover={{scale: 1.05}} whileTap={{scale: 0.95}}>
              <Link
                to="/contact"
                className="bg-gradient-to-r from-forest-600 to-forest-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 border border-forest-500/30"
              >
                Get Started
              </Link>
            </motion.div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center space-x-2">
            <motion.button
              onClick={handleAdminAccess}
              className="p-2 text-gray-700 hover:text-forest-700 transition-colors rounded-lg"
              whileTap={{scale: 0.95}}
              title="Admin Access"
            >
              <SafeIcon icon={FiSettings} className="h-5 w-5" />
            </motion.button>

            <motion.button
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-700 hover:text-forest-700 transition-colors p-2 rounded-lg"
              whileTap={{scale: 0.95}}
            >
              <SafeIcon icon={isOpen ? FiX : FiMenu} className="h-6 w-6" />
            </motion.button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{opacity: 0, height: 0}}
              animate={{opacity: 1, height: 'auto'}}
              exit={{opacity: 0, height: 0}}
              transition={{duration: 0.3}}
              className="md:hidden bg-white/95 backdrop-blur-md rounded-2xl mt-2 mb-4 border border-forest-200 overflow-hidden shadow-xl"
            >
              <div className="px-4 py-6 space-y-3">
                {navItems.map((item) => (
                  <motion.div
                    key={item.name}
                    initial={{opacity: 0, x: -20}}
                    animate={{opacity: 1, x: 0}}
                    transition={{delay: 0.1}}
                  >
                    <Link
                      to={item.path}
                      onClick={() => setIsOpen(false)}
                      className={`flex items-center space-x-3 px-4 py-3 text-base font-medium rounded-xl transition-all duration-300 border ${
                        location.pathname === item.path
                          ? 'text-white bg-gradient-to-r from-forest-600 to-forest-700 shadow-lg border-forest-500/30'
                          : 'text-gray-700 hover:text-forest-700 hover:bg-forest-50 border-transparent hover:border-forest-200'
                      }`}
                    >
                      <SafeIcon icon={item.icon} className="h-5 w-5" />
                      <span>{item.name}</span>
                    </Link>
                  </motion.div>
                ))}

                <motion.div
                  initial={{opacity: 0, x: -20}}
                  animate={{opacity: 1, x: 0}}
                  transition={{delay: 0.2}}
                  className="pt-4"
                >
                  <Link
                    to="/contact"
                    onClick={() => setIsOpen(false)}
                    className="block w-full text-center bg-gradient-to-r from-forest-600 to-forest-700 text-white px-4 py-3 rounded-xl font-semibold shadow-lg border border-forest-500/30"
                  >
                    Get Started
                  </Link>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};

export default Navbar;